import React from 'react';
import { Helmet } from 'react-helmet-async';

//
// 1. Page Title + Meta Description
//
interface PageTitleDescriptionHelmetProps {
  title?: string;
  description?: string;
  keywords?: string;
  defaultTitle?: string;
  defaultDescription?: string;
  defaultKeywords?: string;
}

export const PageTitleDescriptionHelmet: React.FC<PageTitleDescriptionHelmetProps> = ({
  title,
  description,
  keywords,
  defaultKeywords = 'Global24 News, Breaking News, Latest News, World News, Politics News, Business News, Entertainment News, Sports News, Technology News, Lifestyle News',
  defaultTitle = 'Global24 News, Breaking News, Latest News, World News, Politics News, Business News, Entertainment News, Sports News, Technology News, Lifestyle News',
  defaultDescription = 'Global24 News, your source for real-time headlines, trending topics, and worldwide coverage.',
}) => {
  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
    </Helmet>
  );
};

//
// 2. JSON-LD Structured Data for NewsArticle
//
interface NewsArticleJsonLdHelmetProps {
  title: string;
  postTime: number; // UNIX timestamp in seconds
  imgUrl?: string;
  body?: string;
  url?: string;
}

export const NewsArticleJsonLdHelmet: React.FC<NewsArticleJsonLdHelmetProps> = ({
  title,
  postTime,
  imgUrl,
  body,
  url,
}) => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: title,
    datePublished: new Date(postTime * 1000).toISOString(),
    author: {
      "@type": "Organization",
      name: "Global24 News",
    },
    image: imgUrl || undefined,
    articleBody: body?.substring(0, 500),
    mainEntityOfPage: url || (typeof window !== 'undefined' ? window.location.href : ''),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};


//
// 3. Preload Image Helmet
//
interface ImagePreloadHelmetProps {
  baseUrl: string;
}

export const ImagePreloadHelmet: React.FC<ImagePreloadHelmetProps> = ({ baseUrl }) => {
  const preloadUrl = `${baseUrl}?name=large`;

  return (
    <Helmet>
      <link
        rel="preload"
        as="image"
        href={preloadUrl}
        imageSrcSet={`${baseUrl}?name=large 1024w, ${baseUrl}?name=medium 768w, ${baseUrl}?name=small 480w`}
        imageSizes="(min-width: 1024px) 100vw, (min-width: 768px) 100vw, 100vw"
      />
    </Helmet>
  );
};
